import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import './Toast.css';

const ToastContext = createContext(null);

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback((type, message, duration = 3600) => {
    const id = ++toastId;
    setToasts((prev) => [...prev.slice(-4), { id, type, message }]);
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
    return id;
  }, [removeToast]);

  const api = useMemo(
    () => ({
      success: (msg, duration) => pushToast('success', msg, duration),
      error: (msg, duration) => pushToast('error', msg, duration),
      info: (msg, duration) => pushToast('info', msg, duration),
      warning: (msg, duration) => pushToast('warning', msg, duration),
    }),
    [pushToast]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-viewport" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast-item toast-${t.type}`}>
            <FontAwesomeIcon
              icon={
                t.type === 'success'
                  ? faCheckCircle
                  : t.type === 'error' || t.type === 'warning'
                    ? faExclamationCircle
                    : faInfoCircle
              }
              className="toast-icon"
            />
            <span className="toast-message">{t.message}</span>
            <button type="button" className="toast-close" onClick={() => removeToast(t.id)} aria-label="Dismiss">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      success: () => {},
      error: () => {},
      info: () => {},
      warning: () => {},
    };
  }
  return ctx;
}
